import { normalize, bigrams, stripAccents } from "./normalize";
import { expandWithLexicon, canonicalsOf, inflectionOf } from "./lexicon";

/**
 * Motor de recuperação determinístico.
 *
 * Índice invertido em memória sobre a base gerada. Sem LLM, sem ida ao servidor,
 * sem geração de texto: o motor recupera, seleciona e ordena — nunca redige.
 *
 * Alvo de tempo: abaixo de 50 ms por pergunta, no cliente.
 */

export interface KbDoc {
  id: string;
  source: "help" | "tour" | "manual";
  route: string | null;
  screen: string;
  title: string;
  text: string;
  kind: string;
  roles: string[];
  plans: string[] | null;
  terms: string[];
  related?: string[];
}

export interface SearchContext {
  /** Rota em que o usuário está agora. Alimenta o reforço de contexto. */
  route?: string | null;
  role?: string;
  plan?: string;
  /** Assunto corrente da conversa: id do último documento respondido. */
  currentTopicId?: string | null;
}

export interface ScoredDoc {
  doc: KbDoc;
  score: number;
  /**
   * Fração dos termos da pergunta que o documento de fato contém.
   *
   * Existe porque o BM25 mede o quanto casou, não que parte da pergunta foi
   * entendida: "qual o cnpj da empresa de vocês" casa forte em `cnpj` e ignora
   * o resto, produzindo pontuação alta para uma pergunta fora de escopo. Sem
   * este número, nenhum piso separa "receita de bolo" de uma dúvida real —
   * medido, as duas distribuições se sobrepõem.
   */
  coverage: number;
}

export type SearchOutcome =
  | { kind: "answer"; doc: KbDoc; score: number; runnersUp: KbDoc[] }
  | { kind: "disambiguation"; options: KbDoc[] }
  | { kind: "unknown"; nearest: KbDoc[] }
  | { kind: "start"; suggestions: KbDoc[] };

// ─── Cortes ─────────────────────────────────────────────────────────────────

/**
 * Cortes definidos por medição sobre a bateria, nunca por intuição. A varredura
 * vive em `tests/unit/lib/help-engine-calibration.test.ts`.
 *
 * Quatro coisas que a medição mostrou e a intuição não mostraria:
 *
 * 1. Pontuação sozinha NÃO separa dentro de fora do escopo. As distribuições se
 *    sobrepõem, e nenhum piso zerava os falsos positivos.
 * 2. Cobertura por CONTAGEM de termos também não basta: "quanto custa um
 *    iphone" casa dois de três termos e passaria — os dois genéricos, faltando
 *    justamente o que carrega a pergunta.
 * 3. Cobertura PONDERADA POR IDF resolve, porque põe o peso onde está a
 *    informação: um termo que a base não conhece tem IDF máximo e derruba a
 *    cobertura sozinho ao não casar.
 * 4. O corte de termo estrangeiro só funciona depois que as flexões verbais são
 *    reconhecidas. Enquanto "troco" e "encerro" eram tratados como palavras de
 *    fora, perguntas comuns apareciam como metade estrangeiras — e exigir
 *    pouco termo desconhecido custava 20 pontos de taxa de resposta.
 *
 * O requisito de zero falsos positivos é do prompt-fonte e não se negocia: uma
 * resposta confiante e errada faz o usuário agir errado, enquanto um "não sei"
 * com os vizinhos ao lado ainda o leva a algum lugar.
 */
export const THRESHOLDS = {
  /** Abaixo disto, o motor diz que não sabe. */
  floor: 5,
  /** Distância relativa mínima entre 1º e 2º para responder direto. */
  leadRatio: 1.05,
  /** Fração mínima da pergunta que o documento precisa responder. */
  minCoverage: 0.3,
  /**
   * Fração máxima de termos da pergunta que a base sequer conhece.
   *
   * Um termo que não aparece em NENHUM documento é sinal forte de assunto
   * estrangeiro. "quanto custa um iphone" cobria dois terços dos próprios termos
   * — `quanto` e `custa` existem na tela de planos — e recebia resposta
   * confiante. A cobertura sozinha não pega isso: ela mede o que casou, e não
   * o quanto do que sobrou é completamente desconhecido.
   */
  maxUnknownRatio: 0.3,
  /**
   * Cobertura exigida quando a pergunta tem QUALQUER termo que a base não
   * conhece.
   *
   * Os dois sinais isolados não separavam: um corte de fração baixo o bastante
   * para barrar "quanto custa um iphone" (um terço de termo estrangeiro) também
   * derrubava 17% das perguntas legítimas. Combinados, resolvem — quem traz uma
   * palavra de fora precisa acertar bem mais do resto para receber uma resposta
   * confiante.
   */
  minCoverageWithUnknown: 0.3,
};

const K1 = 1.4;
const B = 0.72;
const TITLE_BOOST = 3;
const PHRASE_BONUS = 2.5;
const CONTEXT_BONUS = 1.2;
const TERMS_BOOST = 2;
/** Peso dos termos que o léxico inferiu, contra 1 para os que a pessoa escreveu. */
const LEXICON_WEIGHT = 0.55;
/** Multiplicador do documento da tela em que o usuário está agora. */
const CONTEXT_MULTIPLIER = 1.35;
/**
 * Peso por origem do documento.
 *
 * O passo de tour é uma versão curta e sequencial do que o painel explica por
 * inteiro. Para RESPONDER uma pergunta, o painel é sempre a fonte melhor — o
 * tour ensina a percorrer a tela, não a resolver uma dúvida pontual. Sem esta
 * distinção, ao dobrar a base os passos de tour passaram a roubar o primeiro
 * lugar de seções de painel mais completas.
 */
const SOURCE_WEIGHT: Record<string, number> = { help: 1, manual: 1, tour: 0.65 };

// ─── Índice ─────────────────────────────────────────────────────────────────

interface IndexedDoc {
  doc: KbDoc;
  bodyTokens: string[];
  titleTokens: Set<string>;
  termTokens: Set<string>;
  freq: Map<string, number>;
  length: number;
  phrases: Set<string>;
}

export class HelpEngine {
  private readonly docs: IndexedDoc[] = [];
  private readonly df = new Map<string, number>();
  private readonly avgLength: number;
  private readonly byId = new Map<string, KbDoc>();
  /**
   * Flexão de 1ª pessoa → infinitivo, derivada da própria base.
   *
   * As pessoas perguntam "como troco", "onde emito", "quando cancelo". Um mapa
   * escrito à mão nunca alcança todos esses verbos, e cada um que falta é
   * tratado como palavra estrangeira de peso máximo — foi o que fez "como troco
   * minha senha" ficar com cobertura de 0,38 e quase ser recusada, com a seção
   * "Trocar a senha" em primeiro lugar.
   *
   * Aqui o mapa nasce dos verbos que já existem nos textos: para cada infinitivo
   * indexado, registra-se a forma em -o correspondente.
   */
  private readonly flexoes = new Map<string, string>();

  constructor(docs: KbDoc[]) {
    for (const doc of docs) {
      const bodyTokens = normalize(`${doc.title} ${doc.text}`);
      const freq = new Map<string, number>();
      for (const token of bodyTokens) freq.set(token, (freq.get(token) ?? 0) + 1);
      for (const token of new Set(bodyTokens)) this.df.set(token, (this.df.get(token) ?? 0) + 1);

      this.docs.push({
        doc,
        bodyTokens,
        titleTokens: new Set(normalize(doc.title)),
        termTokens: new Set(doc.terms.flatMap((t) => normalize(t))),
        freq,
        length: bodyTokens.length,
        phrases: new Set(bigrams(normalize(`${doc.title} ${doc.text}`))),
      });
      this.byId.set(doc.id, doc);
    }
    for (const token of this.df.keys()) {
      if (token.length < 5) continue;
      if (!/(ar|er|ir)$/.test(token)) continue;
      const primeiraPessoa = token.slice(0, -2) + "o";
      // Não sobrescreve um termo que já existe por si só na base.
      if (!this.df.has(primeiraPessoa)) this.flexoes.set(primeiraPessoa, token);
    }

    this.avgLength = this.docs.reduce((s, d) => s + d.length, 0) / Math.max(1, this.docs.length);
  }

  get size() {
    return this.docs.length;
  }

  getById(id: string): KbDoc | undefined {
    return this.byId.get(id);
  }

  /**
   * Fração dos termos da pergunta que não existem em documento nenhum.
   *
   * Conta sobre os termos ORIGINAIS, considerando também o que o léxico
   * reconhece: quem escreve "boleto" usa uma palavra que a base entende, ainda
   * que ela não apareça literalmente em nenhum texto.
   */
  unknownRatio(base: string[]): number {
    if (base.length === 0) return 0;
    const desconhecido = (t: string) =>
      !this.df.has(t) &&
      canonicalsOf(t).every((c) => !this.df.has(c)) &&
      !this.df.has(inflectionOf(t) ?? " ") &&
      // A flexão derivada da base também conta como conhecida: "troco" e
      // "encerro" são verbos que a base entende. Sem esta linha, perguntas
      // perfeitamente comuns apareciam como metade estrangeiras.
      !this.flexoes.has(t);
    return base.filter(desconhecido).length / base.length;
  }

  /** IDF de Robertson, com piso para não virar negativo em termo muito comum. */
  private idf(token: string): number {
    const n = this.docs.length;
    const df = this.df.get(token) ?? 0;
    return Math.max(0.05, Math.log(1 + (n - df + 0.5) / (df + 0.5)));
  }

  /**
   * Filtra por papel e plano ANTES de pontuar. O usuário comum nunca vê
   * documento que não é dele — nem como segunda opção, nem como "assunto
   * próximo". Ensinar algo que a pessoa não pode fazer é pior que o silêncio.
   */
  private visible(doc: KbDoc, ctx: SearchContext): boolean {
    // Sem papel declarado, vale o MENOS privilegiado -- nunca "ve tudo".
    //
    // Enquanto a base so tinha telas do usuario comum, omitir o papel era
    // inofensivo. Com a ajuda do painel administrativo dentro dela, a omissao
    // passou a vazar: qualquer chamador que esquecesse o contexto receberia
    // documento de bastidores. E o sintoma nem seria um vazamento visivel --
    // seria a resposta certa perdendo lugar para uma que nao e da pessoa, que
    // foi exatamente como isto apareceu (uma pergunta clara virou ambigua).
    //
    // Falhar fechado aqui nao custa nada: a interface passa o papel de verdade.
    const papel = ctx.role ?? "user";
    if (doc.roles.length && !doc.roles.includes(papel)) return false;
    if (ctx.plan && doc.plans && !doc.plans.includes(ctx.plan)) return false;
    return true;
  }

  rank(query: string, ctx: SearchContext = {}): ScoredDoc[] {
    const base = normalize(query);
    if (base.length === 0) return [];

    const comFlexoes = base.flatMap((t) => {
      const inf = this.flexoes.get(t);
      return inf ? [t, inf] : [t];
    });
    let tokens = expandWithLexicon(comFlexoes);

    // Contexto de conversa: pergunta curta ou sem termo forte herda os termos do
    // assunto corrente. É o que faz "e se ele não pagar?" continuar a conversa.
    if (ctx.currentTopicId && base.length <= 3) {
      const atual = this.byId.get(ctx.currentTopicId);
      if (atual) tokens = [...new Set([...tokens, ...normalize(atual.title)])];
    }

    /** Termos que a pessoa realmente escreveu, para distingui-los dos inferidos. */
    const origem = new Set(base);

    const fraseDaConsulta = bigrams(base);
    const resultados: ScoredDoc[] = [];

    for (const entry of this.docs) {
      if (!this.visible(entry.doc, ctx)) continue;

      let score = 0;
      for (const token of tokens) {
        // O que a pessoa escreveu pesa mais que o que o léxico inferiu. Sem esta
        // distinção, "como excluo um imóvel" respondia sobre cancelar cobrança:
        // o léxico leva excluir→cancelar, e o sinônimo inferido derrotava a
        // palavra literal no título do documento certo.
        const peso = origem.has(token) ? 1 : LEXICON_WEIGHT;
        const tf = entry.freq.get(token) ?? 0;
        if (tf === 0) {
          // Termo declarado em `terms` conta mesmo sem aparecer no corpo: é o
          // vocabulário real do usuário, mapeado à mão.
          if (entry.termTokens.has(token)) score += this.idf(token) * TERMS_BOOST * peso;
          continue;
        }
        const norm = 1 - B + B * (entry.length / this.avgLength);
        const bm25 = this.idf(token) * ((tf * (K1 + 1)) / (tf + K1 * norm));
        // Reforço de título: o mesmo termo pesa mais quando nomeia o assunto.
        score += bm25 * (entry.titleTokens.has(token) ? TITLE_BOOST : 1) * peso;
        if (entry.termTokens.has(token)) score += this.idf(token) * (TERMS_BOOST / 2) * peso;
      }

      if (score === 0) continue;

      // Cobertura: que fração do que a pessoa ESCREVEU este documento responde.
      // Um termo conta como coberto quando ele próprio casa ou quando seu
      // canônico do léxico casa — quem escreve "boleto" e recebe a seção de
      // "cobrança" foi entendido, e punir esse caso derrubaria justamente o que
      // o léxico existe para resolver.
      const cobre = (t: string) => (entry.freq.get(t) ?? 0) > 0 || entry.termTokens.has(t);
      const casou = (t: string) => {
        if (cobre(t)) return true;
        if (canonicalsOf(t).some(cobre)) return true;
        // A flexão também conta: quem escreve "como excluo" está dizendo
        // "excluir", e ignorar isso derrubava a cobertura e fazia o assistente
        // dizer que não sabia com o documento certo em primeiro lugar.
        const infinitivo = inflectionOf(t) ?? this.flexoes.get(t);
        return !!infinitivo && (cobre(infinitivo) || canonicalsOf(infinitivo).some(cobre));
      };

      // Cobertura PONDERADA POR IDF, não contagem simples de termos.
      //
      // "quanto custa um iphone" casa dois de três termos e passaria por
      // contagem — mas os dois que casam ("quanto", "custa") são genéricos e
      // aparecem em meia base, enquanto o termo que de fato carrega a pergunta
      // não existe em documento nenhum. Pesar pelo IDF põe a massa de
      // informação onde ela está: um termo desconhecido tem IDF máximo e, ao
      // não casar, derruba a cobertura sozinho.
      let pesoTotal = 0;
      let pesoCasado = 0;
      for (const t of base) {
        const peso = this.idf(t);
        pesoTotal += peso;
        if (casou(t)) pesoCasado += peso;
      }
      const coverage = pesoTotal > 0 ? pesoCasado / pesoTotal : 0;

      // Reforço de frase: separa "cancelar cobrança" de dois documentos que só
      // falam de "cancelar".
      for (const par of fraseDaConsulta) {
        if (entry.phrases.has(par)) score += PHRASE_BONUS;
      }

      // Reforço de contexto: a mesma pergunta responde diferente conforme a tela.
      // Multiplicativo, não aditivo: um bônus fixo pequeno não vira o resultado
      // quando a diferença de pontuação é grande, e "estou nesta tela" é um sinal
      // forte sobre do que a pessoa está falando.
      if (ctx.route && entry.doc.route === ctx.route) {
        score = score * CONTEXT_MULTIPLIER + CONTEXT_BONUS;
      }

      score *= SOURCE_WEIGHT[entry.doc.source] ?? 1;

      resultados.push({ doc: entry.doc, score, coverage });
    }

    return resultados.sort((a, b) => b.score - a.score);
  }

  /**
   * As quatro saídas possíveis, e nenhuma outra.
   */
  search(query: string, ctx: SearchContext = {}): SearchOutcome {
    const limpo = query.trim();

    // Vazio, saudação ou pergunta sem conteúdo: menu de partida.
    if (!limpo || isGreeting(limpo) || normalize(limpo).length === 0) {
      return { kind: "start", suggestions: this.suggestionsFor(ctx) };
    }

    const rankeados = this.rank(limpo, ctx);
    const primeiro = rankeados[0];
    const segundo = rankeados[1];
    const desconhecidos = this.unknownRatio(normalize(limpo));
    const estrangeira = desconhecidos > THRESHOLDS.maxUnknownRatio;
    const coberturaExigida = desconhecidos > 0
      ? THRESHOLDS.minCoverageWithUnknown
      : THRESHOLDS.minCoverage;

    // Abaixo do piso, ou entendendo pouco da pergunta: "não sei", com os três
    // assuntos mais próximos. "Não sei" sem saída é abandono.
    if (
      !primeiro ||
      estrangeira ||
      primeiro.score < THRESHOLDS.floor ||
      primeiro.coverage < coberturaExigida
    ) {
      return { kind: "unknown", nearest: rankeados.slice(0, 3).map((r) => r.doc) };
    }

    // Primeiro e segundo empatados: perguntar é melhor que adivinhar.
    if (segundo && primeiro.score / segundo.score < THRESHOLDS.leadRatio) {
      return { kind: "disambiguation", options: rankeados.slice(0, 3).map((r) => r.doc) };
    }

    return {
      kind: "answer",
      doc: primeiro.doc,
      score: primeiro.score,
      runnersUp: rankeados.slice(1, 4).map((r) => r.doc),
    };
  }

  /** Assuntos da tela atual, para abrir a conversa sem tela em branco. */
  suggestionsFor(ctx: SearchContext): KbDoc[] {
    const daTela = this.docs
      .filter((d) => this.visible(d.doc, ctx) && ctx.route && d.doc.route === ctx.route)
      .map((d) => d.doc);
    if (daTela.length) return daTela.slice(0, 5);
    return this.docs
      .filter((d) => this.visible(d.doc, ctx) && d.doc.source === "manual")
      .map((d) => d.doc)
      .slice(0, 5);
  }
}

const SAUDACOES = [
  "oi", "ola", "bom dia", "boa tarde", "boa noite", "eai", "e ai", "opa",
  "tudo bem", "hey", "hi", "teste", "obrigado", "valeu",
];

export function isGreeting(texto: string): boolean {
  const limpo = stripAccents(texto.toLowerCase()).replace(/[^a-z\s]/g, "").trim();
  return SAUDACOES.includes(limpo);
}
